import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { ArrowLeft, Edit2, AlertTriangle, Lightbulb, Sparkles, FileText, X } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import AIScorePanel from '../AIScorePanel';
import { SourceList } from '../SourceChip';
import { useAiUnread } from '../../contexts/AiUnreadContext';
import { hasLensItemAiAnalysis } from '../../utils/aiAnalysisPresence';
import { FormCheckOwnIndicator } from '../InputFeedbackIndicator';

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
  color: white;

  html[data-platform='tizen'] & {
    background: #12121a;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px 16px;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
`;

const TypeBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 10px;
  background: ${props => props.$color}18;
  color: ${props => props.$color};
  border: 1px solid ${props => props.$color}44;
  font-size: 0.8rem;
  font-weight: 600;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
  }
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 32px 32px;
`;

const Content = styled.div`
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.3;
`;

const DescriptionText = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
`;

const Panel = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 16px 18px;
`;

const PanelTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.4);
`;

const PivotLink = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: background 0.15s ease;

  &:hover {
    background: ${props => props.$clickable ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.04)'};
  }
`;

const PivotName = styled.span`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  flex: 1;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetaLabel = styled.span`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const MetaValue = styled.span`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SourceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  background: ${props => props.$bg};
  color: ${props => props.$color};
`;

const TabRow = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
`;

const Tab = styled.button`
  padding: 6px 14px;
  border-radius: 8px;
  border: none;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.12)' : 'transparent'};
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.4)'};
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const TYPE_CONFIG = {
  obstacle: { icon: AlertTriangle, color: '#E94B6E' },
  opportunity: { icon: Lightbulb, color: '#A6D64D' },
};

const SOURCE_CONFIG = {
  manual: { icon: FileText, bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' },
  ai: { icon: Sparkles, bg: 'rgba(156,39,176,0.15)', color: '#9C27B0' },
  rag: { icon: FileText, bg: 'rgba(33,150,243,0.15)', color: '#2196F3' },
};

/**
 * LensItemDetail - Full detail view for an obstacle or opportunity.
 * Shows description, connected pivot point, metadata, RAG references.
 * Maps to Figma Screens 7, 7b, 7c (tabs switch between content sections).
 */
export default function LensItemDetail({
  item,
  pivotPoint,
  scenario,
  relatedItems = [],
  onBack,
  onEdit,
  currentDeviceId = null,
  showDescription = true,
  formCheckPending = false,
  formCheckDismissed = false,
  onDismissFormCheck,
}) {
  const { t } = useTranslation();
  const { markRead } = useAiUnread();
  const isOwn = !item?.created_by || item.created_by === currentDeviceId;
  const [activeTab, setActiveTab] = useState('info');
  const typeConfig = TYPE_CONFIG[item?.type] || TYPE_CONFIG.obstacle;
  const sourceKey = item?.source || 'manual';
  const sourceConfig = SOURCE_CONFIG[sourceKey] || SOURCE_CONFIG.manual;
  const typeLabel = item?.type === 'opportunity' ? t('lenses.opportunity') : t('lenses.obstacle');
  const sourceLabel = sourceKey === 'ai'
    ? t('lenses.sourceAiGenerated')
    : sourceKey === 'rag'
      ? t('lenses.sourceResearch')
      : t('lenses.sourceManual');
  const TypeIcon = typeConfig.icon;
  const SourceIcon = sourceConfig.icon;
  const ragData = item?.rag_references || {};
  const references = ragData.references || (Array.isArray(ragData) ? ragData : []);
  const scores = ragData.scores || null;

  useEffect(() => {
    if (item?.id && hasLensItemAiAnalysis(item)) {
      markRead('lens_item', item.id);
    }
  }, [item?.id, item?.rag_references, item?.ai_confidence, markRead]);

  return (
    <Container>
      <HeaderBar>
        <HeaderLeft>
          <TypeBadge $color={typeConfig.color}>
            <TypeIcon size={14} />
            {typeLabel}
          </TypeBadge>
        </HeaderLeft>
        <HeaderLeft style={{ gap: 8 }}>
          {onEdit && isOwn && (
            <EditButton onClick={() => onEdit(item)}>
              <Edit2 size={14} />
              {t('common.edit')}
            </EditButton>
          )}
          {onBack && (
            <BackButton onClick={onBack} style={{ padding: 8 }}>
              <X size={16} />
            </BackButton>
          )}
        </HeaderLeft>
      </HeaderBar>

      <ScrollArea>
        <Content>
          <FormCheckOwnIndicator
            isOwn={isOwn}
            formCheck={item?.rag_references?.formCheck}
            pending={formCheckPending}
            dismissed={formCheckDismissed}
            onDismiss={onDismissFormCheck}
            variant="border"
            fill
          >
            <Title>{item?.title}</Title>
          </FormCheckOwnIndicator>

          <TabRow>
            <Tab $active={activeTab === 'info'} onClick={() => setActiveTab('info')}>{t('lenses.tabDetails')}</Tab>
            <Tab $active={activeTab === 'related'} onClick={() => setActiveTab('related')}>
              {t('lenses.tabConnections', { count: relatedItems.length })}
            </Tab>
            {references.length > 0 && (
              <Tab $active={activeTab === 'sources'} onClick={() => setActiveTab('sources')}>
                {t('lenses.tabSources', { count: references.length })}
              </Tab>
            )}
          </TabRow>

          {activeTab === 'info' && (
            <>
              {showDescription && item?.description && (
                <DescriptionText>{item.description}</DescriptionText>
              )}

              {(scores || references.length > 0) && (
                <Panel>
                  <AIScorePanel
                    scores={scores}
                    sources={references.map((ref, idx) => ({
                      citation: ref.title || ref.source || t('common.unknownSource'),
                      marker: `[${idx + 1}]`,
                      relevance: ref.similarity ? Math.round(ref.similarity * 100) : ref.relevance || null,
                      excerpt: ref.excerpt || ref.content?.substring(0, 300) || null,
                      chunkId: ref.chunkId || ref.chunk_id || null,
                      documentId: ref.documentId || ref.document_id || null,
                    }))}
                  />
                </Panel>
              )}

              {false && references.length > 0 && ( /* SourceList rendered inside AIScorePanel now */
                <Panel>
                  <PanelTitle>Tutkimuslähteet</PanelTitle>
                  <SourceList
                    sources={references.map((ref, idx) => ({
                      citation: ref.title || ref.source || t('common.unknownSource'),
                      marker: `[${idx + 1}]`,
                      relevance: ref.similarity ? Math.round(ref.similarity * 100) : ref.relevance || null,
                      excerpt: ref.excerpt || ref.content?.substring(0, 300) || null,
                      chunkId: ref.chunkId || ref.chunk_id || null,
                      documentId: ref.documentId || ref.document_id || null,
                    }))}
                    showRelevance
                  />
                </Panel>
              )}

              {pivotPoint && (
                <Panel>
                  <PanelTitle>{t('lenses.pivotLabel')}</PanelTitle>
                  <PivotLink>
                    <PivotName>{pivotPoint.title}</PivotName>
                  </PivotLink>
                </Panel>
              )}

              <Panel>
                <PanelTitle>{t('lenses.metadata')}</PanelTitle>
                <MetaGrid>
                  <MetaItem>
                    <MetaLabel>{t('common.sourceTitleFallback')}</MetaLabel>
                    <MetaValue>
                      <SourceBadge $bg={sourceConfig.bg} $color={sourceConfig.color}>
                        <SourceIcon size={10} />
                        {sourceLabel}
                      </SourceBadge>
                    </MetaValue>
                  </MetaItem>
                  {item?.ai_confidence != null && (
                    <MetaItem>
                      <MetaLabel>{t('lenses.aiConfidence')}</MetaLabel>
                      <MetaValue>{Math.round(item.ai_confidence * 100)}%</MetaValue>
                    </MetaItem>
                  )}
                  {item?.created_by && (
                    <MetaItem>
                      <MetaLabel>{t('lenses.addedBy')}</MetaLabel>
                      <MetaValue>{item.created_by}</MetaValue>
                    </MetaItem>
                  )}
                  {scenario && (
                    <MetaItem>
                      <MetaLabel>{t('lenses.lensFallback')}</MetaLabel>
                      <MetaValue>{scenario.name || scenario}</MetaValue>
                    </MetaItem>
                  )}
                </MetaGrid>
              </Panel>
            </>
          )}

          {activeTab === 'related' && (
            <Panel>
              <PanelTitle>{t('lenses.relatedItems')}</PanelTitle>
              {relatedItems.length === 0 ? (
                <DescriptionText style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.3)' }}>
                  {t('lenses.noRelatedItems')}
                </DescriptionText>
              ) : (
                relatedItems.map(rel => {
                  const relConfig = TYPE_CONFIG[rel.type] || TYPE_CONFIG.obstacle;
                  const RelIcon = relConfig.icon;
                  return (
                    <PivotLink key={rel.id} style={{ marginBottom: 8 }}>
                      <RelIcon size={14} style={{ color: relConfig.color, flexShrink: 0 }} />
                      <PivotName>{rel.title}</PivotName>
                    </PivotLink>
                  );
                })
              )}
            </Panel>
          )}

          {activeTab === 'sources' && references.length > 0 && (
            <Panel>
              <AIScorePanel
                scores={scores}
                sources={references.map((ref, idx) => ({
                  citation: ref.title || ref.source || t('common.unknownSource'),
                  marker: `[${idx + 1}]`,
                  relevance: ref.similarity ? Math.round(ref.similarity * 100) : ref.relevance || null,
                  excerpt: ref.excerpt || ref.content?.substring(0, 300) || null,
                  chunkId: ref.chunkId || ref.chunk_id || null,
                  documentId: ref.documentId || ref.document_id || null,
                }))}
                defaultTab="refs"
              />
            </Panel>
          )}
        </Content>
      </ScrollArea>
    </Container>
  );
}
